import { useEffect } from "react";
import { useReactFlow } from "@xyflow/react";

/** Keyboard focus on canvas content stays visible without moving focus or writing content. */
export function CanvasFocusVisibility({
  root,
  contentHost,
}: {
  readonly root: HTMLElement | null;
  readonly contentHost: HTMLElement | null;
}): null {
  const flow = useReactFlow();
  useEffect(() => {
    if (!root || !contentHost) return;
    let keyboard = false;
    const key = (event: KeyboardEvent): void => {
      if (
        [
          "Tab",
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Home",
          "End",
          "PageUp",
          "PageDown",
        ].includes(event.key)
      )
        keyboard = true;
    };
    const pointer = (): void => {
      keyboard = false;
    };
    const focus = (event: FocusEvent): void => {
      const target = event.target;
      if (
        !keyboard ||
        !(target instanceof HTMLElement) ||
        !contentHost.contains(target)
      )
        return;
      const frame = root.getBoundingClientRect();
      const box = target.getBoundingClientRect();
      if (
        frame.width <= 0 ||
        frame.height <= 0 ||
        box.width <= 0 ||
        box.height <= 0
      )
        return;
      if (
        box.left >= frame.left &&
        box.right <= frame.right &&
        box.top >= frame.top &&
        box.bottom <= frame.bottom
      )
        return;
      const point = flow.screenToFlowPosition({
        x: (box.left + box.right) / 2,
        y: (box.top + box.bottom) / 2,
      });
      void flow.setCenter(point.x, point.y, {
        zoom: flow.getZoom(),
        duration: 0,
      });
    };
    document.addEventListener("keydown", key, true);
    document.addEventListener("pointerdown", pointer, true);
    root.addEventListener("focusin", focus);
    return () => {
      document.removeEventListener("keydown", key, true);
      document.removeEventListener("pointerdown", pointer, true);
      root.removeEventListener("focusin", focus);
    };
  }, [root, contentHost, flow]);
  return null;
}
